/**
 * Admin Gallery API module — CON-CMS-004.
 *
 * Endpoints covered:
 *   GET    /api/v1/admin/cms/gallery                      — list all images
 *   POST   /api/v1/admin/cms/gallery                      — create single image
 *   PATCH  /api/v1/admin/cms/gallery/:id                  — update single image
 *   DELETE /api/v1/admin/cms/gallery/:id                  — delete single image
 *   PATCH  /api/v1/admin/cms/gallery/reorder              — reorder images
 *   POST   /api/v1/admin/cms/gallery/bulk-upload          — bulk image upload
 *   DELETE /api/v1/admin/cms/gallery/bulk-delete          — bulk delete
 *   PATCH  /api/v1/admin/cms/gallery/bulk-change-category — bulk category change
 *
 * Backend dependency: PL-CMS gallery endpoints. If these return 404 in dev,
 * the platform ticket for gallery CRUD has not yet shipped.
 */
import { apiClient } from '@dfq/shared/api/client';
import { throwAsApiError, unwrap } from '@dfq/shared/api/_envelope';
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  BulkChangeCategoryPayload,
  BulkDeleteGalleryPayload,
  CreateGalleryImagePayload,
  GalleryImageDTO,
  GalleryListResponse,
  ReorderGalleryPayload,
  UpdateGalleryImagePayload,
} from '@dfq/shared/types/cms';

// ---------------------------------------------------------------------------
// GET /api/v1/admin/cms/gallery
// ---------------------------------------------------------------------------

export async function fetchAdminGallery(): Promise<GalleryListResponse> {
  try {
    const res = await apiClient.get<ApiEnvelope<GalleryListResponse>>(
      '/admin/cms/gallery',
    );
    return unwrap(res.data, res.status);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/gallery
// ---------------------------------------------------------------------------

export async function createAdminGalleryImage(
  payload: CreateGalleryImagePayload,
): Promise<GalleryImageDTO> {
  try {
    const res = await apiClient.post<ApiEnvelope<{ image: GalleryImageDTO }>>(
      '/admin/cms/gallery',
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.image;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/admin/cms/gallery/:id
// ---------------------------------------------------------------------------

export async function updateAdminGalleryImage(
  id: number,
  payload: UpdateGalleryImagePayload,
): Promise<GalleryImageDTO> {
  try {
    const res = await apiClient.patch<ApiEnvelope<{ image: GalleryImageDTO }>>(
      `/admin/cms/gallery/${id}`,
      payload,
    );
    const data = unwrap(res.data, res.status);
    return data.image;
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/admin/cms/gallery/:id
// ---------------------------------------------------------------------------

export async function deleteAdminGalleryImage(id: number): Promise<void> {
  try {
    await apiClient.delete(`/admin/cms/gallery/${id}`);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/admin/cms/gallery/reorder
// ---------------------------------------------------------------------------

export async function reorderAdminGallery(
  payload: ReorderGalleryPayload,
): Promise<void> {
  try {
    await apiClient.patch('/admin/cms/gallery/reorder', payload);
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/admin/cms/gallery/bulk-upload
// ---------------------------------------------------------------------------

export async function bulkUploadAdminGallery(
  files: File[],
  category: GalleryImageDTO['category'],
  onProgress?: (fileIndex: number, percent: number) => void,
): Promise<GalleryImageDTO[]> {
  const results: GalleryImageDTO[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const form = new FormData();
    form.append('image', file);
    form.append('category', category);
    form.append('title', file.name.replace(/\.[^.]+$/, ''));

    try {
      const res = await apiClient.post<ApiEnvelope<{ image: GalleryImageDTO }>>(
        '/admin/cms/gallery/bulk-upload',
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (event) => {
            const percent = event.total
              ? Math.round((event.loaded / event.total) * 100)
              : 0;
            onProgress?.(i, percent);
          },
        },
      );
      const data = unwrap(res.data, res.status);
      results.push(data.image);
    } catch (err) {
      throwAsApiError(err);
    }
  }

  return results;
}

// ---------------------------------------------------------------------------
// DELETE /api/v1/admin/cms/gallery/bulk-delete
// ---------------------------------------------------------------------------

export async function bulkDeleteAdminGallery(
  payload: BulkDeleteGalleryPayload,
): Promise<void> {
  try {
    await apiClient.delete('/admin/cms/gallery/bulk-delete', {
      data: payload,
    });
  } catch (err) {
    throwAsApiError(err);
  }
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/admin/cms/gallery/bulk-change-category
// ---------------------------------------------------------------------------

export async function bulkChangeCategoryAdminGallery(
  payload: BulkChangeCategoryPayload,
): Promise<void> {
  try {
    await apiClient.patch(
      '/admin/cms/gallery/bulk-change-category',
      payload,
    );
  } catch (err) {
    throwAsApiError(err);
  }
}

export const galleryAdminApiClient = {
  fetchGallery: fetchAdminGallery,
  createImage: createAdminGalleryImage,
  updateImage: updateAdminGalleryImage,
  deleteImage: deleteAdminGalleryImage,
  reorder: reorderAdminGallery,
  bulkUpload: bulkUploadAdminGallery,
  bulkDelete: bulkDeleteAdminGallery,
  bulkChangeCategory: bulkChangeCategoryAdminGallery,
};
